import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import TaxProfilesManager from '@apis/taxProfiles/TaxProfilesManager'
import { LOADING, SUCCEEDED, FAILED, IDLE } from '@constants/fetchStatuses'

const initialState = {
  createTaxProfile: {
    status: IDLE,
    data: {},
    error: null,
  },
  updateTaxProfile: {
    status: IDLE,
    data: {},
    error: null,
  },
  taxProfiles: {
    status: IDLE,
    data: [],
    error: null,
  },
  w9File: {
    status: IDLE,
    data: {},
    error: null,
  },
}

// Internal thunk that expects object parameter
const _createTaxProfileInternal = createAsyncThunk('taxProfiles/createTaxProfile', async ({ taxInformation, jwt = null }, { rejectWithValue }) => {
  try {
    const response = await TaxProfilesManager.createTaxProfile(taxInformation, jwt)
    return response
  } catch (err) {
    return rejectWithValue('Error creating tax profile')
  }
})

// Wrapper function that accepts separate parameters for convenience
export const createTaxProfile = (taxInformation, jwt = null) => {
  return _createTaxProfileInternal({ taxInformation, jwt })
}

export const updateTaxProfile = createAsyncThunk('taxProfiles/updateTaxProfile', async (taxProfile, { rejectWithValue }) => {
  try {
    const response = await TaxProfilesManager.updateTaxProfile(taxProfile)
    return response
  } catch (err) {
    return rejectWithValue('Error creating tax profile')
  }
})

export const fetchTaxProfiles = createAsyncThunk('taxProfiles/fetchTaxProfiles', async (companyId, { rejectWithValue }) => {
  try {
    const response = await TaxProfilesManager.getTaxProfiles(companyId)
    return response.results
  } catch (err) {
    return rejectWithValue('Error fetching tax profiles')
  }
})

export const uploadW9File = createAsyncThunk('taxProfiles/uploadW9File', async ({ companyId, file, jwt = null }, { rejectWithValue }) => {
  try {
    const response = await TaxProfilesManager.uploadW9File(companyId, file, jwt)
    return response
  } catch (err) {
    return rejectWithValue('Error uploading W9 file')
  }
})


const taxProfilesSlice = createSlice({
  name: 'taxProfiles',
  initialState,
  reducers: {
    resetCreateTaxProfile: state => {
      state.createTaxProfile.status = initialState.createTaxProfile.status
      state.createTaxProfile.data = initialState.createTaxProfile.data
      state.createTaxProfile.error = initialState.createTaxProfile.error
    },
    resetUpdateTaxProfile: state => {
      state.updateTaxProfile.status = initialState.updateTaxProfile.status
      state.updateTaxProfile.data = initialState.updateTaxProfile.data
      state.updateTaxProfile.error = initialState.updateTaxProfile.error
    },
    resetTaxProfiles: state => {
      state.taxProfiles.status = initialState.taxProfiles.status
      state.taxProfiles.data = initialState.taxProfiles.data
      state.taxProfiles.error = initialState.taxProfiles.error
    },
    resetW9File: state => {
      state.w9File.status = initialState.w9File.status
      state.w9File.data = initialState.w9File.data
      state.w9File.error = initialState.w9File.error
    },
  },
  extraReducers: {
    [_createTaxProfileInternal.pending]: state => {
      state.createTaxProfile.status = LOADING
    },
    [_createTaxProfileInternal.fulfilled]: (state, action) => {
      state.createTaxProfile.status = SUCCEEDED
      state.createTaxProfile.data = action.payload
      // add tax profile to taxProfiles.data
      state.taxProfiles.data.push(action.payload)
    },
    [_createTaxProfileInternal.rejected]: (state, action) => {
      state.createTaxProfile.status = FAILED
      state.createTaxProfile.error = action.error
    },
    [updateTaxProfile.pending]: state => {
      state.updateTaxProfile.status = LOADING
    },
    [updateTaxProfile.fulfilled]: (state, action) => {
      state.updateTaxProfile.status = SUCCEEDED
      state.updateTaxProfile.data = action.payload
      // update tax profile in taxProfiles.data
      if (action.payload.is_deleted) {
        state.taxProfiles.data = state.taxProfiles.data.filter(taxProfile => taxProfile._id !== action.payload._id)
      } else {
        const index = state.taxProfiles.data.findIndex(taxProfile => taxProfile._id === action.payload._id)
        state.taxProfiles.data[index] = action.payload
      }
    },
    [updateTaxProfile.rejected]: (state, action) => {
      state.updateTaxProfile.status = FAILED
      state.updateTaxProfile.error = action.error
    },
    [fetchTaxProfiles.pending]: state => {
      state.taxProfiles.status = LOADING
    },
    [fetchTaxProfiles.fulfilled]: (state, action) => {
      state.taxProfiles.status = SUCCEEDED
      state.taxProfiles.data = action.payload
    },
    [fetchTaxProfiles.rejected]: (state, action) => {
      state.taxProfiles.status = FAILED
      state.taxProfiles.error = action.error
    },
    [uploadW9File.pending]: state => {
      state.w9File.status = LOADING
    },
    [uploadW9File.fulfilled]: (state, action) => {
      state.w9File.status = SUCCEEDED
      state.w9File.data = action.payload
    },
    [uploadW9File.rejected]: (state, action) => {
      state.w9File.status = FAILED
      state.w9File.error = action.error
    },
  },
})

export const {
  resetCreateTaxProfile,
  resetUpdateTaxProfile,
  resetTaxProfiles,
  resetW9File,
} = taxProfilesSlice.actions

export default taxProfilesSlice.reducer
